import { useState, useEffect } from "react";
import styles from "./registrations-viewer-modal.module.css";
import { toast } from "react-toastify";
import { api } from "@/utils/apiService";
import { useAuth } from "@/components/context/AuthContext";

export default function RegistrationsViewerModal({ tournamentId, onClose }) {
  const { token } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  useEffect(() => {
    fetchRegistrations(currentPage);
  }, [currentPage]);

  const fetchRegistrations = async (page) => {
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/tournaments/${tournamentId}/registrations`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page,
            limit: 20,
          },
        },
      );

      setRegistrations(res.data.data.registrations);
      setPagination(res.data.data.pagination);
    } catch (error) {
      console.log("Error Fetching Registrations ::", error);
      const errMssg =
        error?.response?.data?.data?.message || "Error Fetching Registrations";
      toast.error(errMssg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "#00d4ff";
      case "pending":
        return "#ffa500";
      case "cancelled":
        return "#ff6b6b";
      default:
        return "#808080";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.pages) {
      setCurrentPage(page);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = Math.ceil(pagination.total, 20);
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const endPage = Math.min(pagination.pages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className={styles.pageButton}
          onClick={() => handlePageChange(1)}
        >
          1
        </button>,
      );
      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" className={styles.ellipsis}>
            ...
          </span>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`${styles.pageButton} ${currentPage === i ? styles.active : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>,
      );
    }

    if (endPage < pagination.pages) {
      if (endPage < pagination.pages - 1) {
        buttons.push(
          <span key="end-ellipsis" className={styles.ellipsis}>
            ...
          </span>,
        );
      }
      buttons.push(
        <button
          key={pagination.pages}
          className={styles.pageButton}
          onClick={() => handlePageChange(pagination.pages)}
        >
          {pagination.pages}
        </button>,
      );
    }

    return buttons;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2>Tournament Registrations</h2>
            <p className={styles.subtitle}>
              Total: {pagination.total} registrations | Page{" "}
              {pagination.current} of {pagination.pages}
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.modalContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading registrations...</p>
            </div>
          ) : registrations.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📋</div>
              <p>No registrations found</p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.registrationsTable}>
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Game ID</th>
                    <th>Team</th>
                    <th>Rank</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg) => (
                    <tr key={reg._id}>
                      <td>
                        <div className={styles.userInfo}>
                          <div className={styles.username}>
                            {reg.user.username || "-"}
                          </div>
                          <div className={styles.email}>{reg.user.email}</div>
                        </div>
                      </td>
                      <td>
                        <code className={styles.gameId}>
                          {reg.gameId || "-"}
                        </code>
                      </td>
                      <td>
                        <span className={styles.teamName}>
                          {reg.teamName || "-"}
                        </span>
                      </td>
                      <td>
                        <span className={styles.rank}>
                          {reg.playerRank || "-"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={styles.statusBadge}
                          style={{
                            backgroundColor: getStatusColor(reg.status),
                          }}
                        >
                          {reg.status}
                        </span>
                      </td>
                      <td>
                        <span className={styles.paymentMethod}>
                          {reg.paymentMethod}
                        </span>
                      </td>
                      <td>
                        <span className={styles.date}>
                          {reg.registeredAt
                            ? formatDate(reg.registeredAt)
                            : "-"}
                        </span>
                      </td>
                      <td>
                        <button
                          className={styles.viewButton}
                          onClick={() => setSelectedRegistration(reg)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && registrations.length > 0 && (
          <div className={styles.paginationContainer}>
            <button
              className={styles.navButton}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className={styles.pageButtons}>
              {renderPaginationButtons()}
            </div>

            <button
              className={styles.navButton}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
            >
              Next
            </button>
          </div>
        )}

        {selectedRegistration && (
          <div
            className={styles.detailOverlay}
            onClick={() => setSelectedRegistration(null)}
          >
            <div
              className={styles.detailModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.detailHeader}>
                <h3>Registration Details</h3>
                <button
                  className={styles.closeButton}
                  onClick={() => setSelectedRegistration(null)}
                >
                  ✕
                </button>
              </div>
              <div className={styles.detailContent}>
                <div className={styles.detailSection}>
                  <h4>Player Information</h4>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Username:</span>
                      <span className={styles.detailValue}>
                        {selectedRegistration.user.username || "-"}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Email:</span>
                      <span className={styles.detailValue}>
                        {selectedRegistration.user.email}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Game ID:</span>
                      <span className={styles.detailValue}>
                        <code>{selectedRegistration.gameId || "-"}</code>
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Player Rank:</span>
                      <span className={styles.detailValue}>
                        {selectedRegistration.playerRank || "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.detailSection}>
                  <h4>Team Information</h4>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Team Name:</span>
                      <span className={styles.detailValue}>
                        {selectedRegistration.teamName || "No team"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.detailSection}>
                  <h4>Payment Information</h4>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Status:</span>
                      <span
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: getStatusColor(
                            selectedRegistration.status,
                          ),
                        }}
                      >
                        {selectedRegistration.status}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        Payment Method:
                      </span>
                      <span className={styles.detailValue}>
                        {selectedRegistration.paymentMethod}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        Registration Price:
                      </span>
                      <span className={styles.detailValue}>
                        ₹{selectedRegistration.registrationPrice}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.detailSection}>
                  <h4>Timeline</h4>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Registered At:</span>
                      <span className={styles.detailValue}>
                        {selectedRegistration.registeredAt
                          ? formatDate(selectedRegistration.registeredAt)
                          : "-"}
                      </span>
                    </div>
                    {selectedRegistration.paidAt && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Paid At:</span>
                        <span className={styles.detailValue}>
                          {formatDate(selectedRegistration.paidAt)}
                        </span>
                      </div>
                    )}
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Created At:</span>
                      <span className={styles.detailValue}>
                        {formatDate(selectedRegistration.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
